import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { BottomNav } from "@/components/BottomNav";
import { 
  ArrowLeft, Home, Search, Droplet, LineChart, MessageCircle,
  TrendingUp, TrendingDown, CheckCircle, Info, Download, FileText, Loader2
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { toast } from "sonner";
import jsPDF from "jspdf";

// Lana Avatar URL for report
const LANA_AVATAR_URL = "https://customer-assets.emergentagent.com/job_glucose-insights-2/artifacts/y7q9fk4i_ChatGPT%20Image%20Mar%205%2C%202026%2C%2004_39_46%20AM.png";

const Insights = () => {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [glucoseLogs, setGlucoseLogs] = useState([]);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportRange, setReportRange] = useState("7");
  const [customRange, setCustomRange] = useState({
    start: "",
    end: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [insightsRes, glucoseRes] = await Promise.all([
          fetch(`${API}/insights`, {
            headers: { Authorization: `Bearer ${token}` },
            
          }),
          fetch(`${API}/glucose?days=14`, {
            headers: { Authorization: `Bearer ${token}` },
            
          }),
        ]);

        if (insightsRes.ok) {
          const data = await insightsRes.json();
          setInsights(data);
        }

        if (glucoseRes.ok) {
          const data = await glucoseRes.json();
          setGlucoseLogs(data);
        }
      } catch (error) {
        console.error("Fetch insights error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Prepare chart data
  const chartData = glucoseLogs
    .slice()
    .reverse()
    .map((log) => ({
      time: new Date(log.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      value: log.glucose_value,
    }));

  // Prepare daily pattern data
  const dailyPatternData = insights?.daily_pattern 
    ? Object.entries(insights.daily_pattern).map(([type, value]) => ({
        name: type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: Math.round(value),
      }))
    : [];

  const getDateRange = () => {
    const end = new Date();
    let start = new Date();
    
    if (reportRange === "custom") {
      return {
        start: customRange.start,
        end: customRange.end
      };
    }
    
    const days = parseInt(reportRange);
    start.setDate(end.getDate() - days);
    
    return {
      start: start.toISOString(),
      end: end.toISOString()
    };
  };

  const generatePDF = async () => {
    setReportLoading(true);
    
    try {
      const { start, end } = getDateRange();
      
      // Fetch report data from backend
      const response = await fetch(`${API}/report/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({ start_date: start, end_date: end }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate report");
      }

      const reportData = await response.json();
      
      // Get translations for PDF - use English as fallback for PDF content
      const pdfText = {
        title: t('report.title') !== 'report.title' ? t('report.title') : 'PlateMind Health Report',
        preparedFor: t('report.prepared_for') !== 'report.prepared_for' ? t('report.prepared_for') : 'Prepared for',
        reportPeriod: t('report.report_period') !== 'report.report_period' ? t('report.report_period') : 'Report Period',
        generated: t('report.generated') !== 'report.generated' ? t('report.generated') : 'Generated',
        glucoseSummary: t('report.glucose_summary') !== 'report.glucose_summary' ? t('report.glucose_summary') : 'Glucose Summary',
        avgGlucose: t('report.avg_glucose') !== 'report.avg_glucose' ? t('report.avg_glucose') : 'Average Glucose',
        highestReading: t('report.highest_reading') !== 'report.highest_reading' ? t('report.highest_reading') : 'Highest Reading',
        lowestReading: t('report.lowest_reading') !== 'report.lowest_reading' ? t('report.lowest_reading') : 'Lowest Reading',
        totalReadings: t('report.total_readings') !== 'report.total_readings' ? t('report.total_readings') : 'Total Readings',
        timeInRange: t('report.time_in_range') !== 'report.time_in_range' ? t('report.time_in_range') : 'Time in Range (70-180 mg/dL)',
        mealInsights: t('report.meal_insights') !== 'report.meal_insights' ? t('report.meal_insights') : 'Meal Insights',
        totalMeals: t('report.total_meals') !== 'report.total_meals' ? t('report.total_meals') : 'Total Meals Logged',
        avgCarbs: t('report.avg_carbs') !== 'report.avg_carbs' ? t('report.avg_carbs') : 'Average Carbs per Meal',
        totalCarbs: t('report.total_carbs') !== 'report.total_carbs' ? t('report.total_carbs') : 'Total Carbohydrates',
        highSpikeMeals: t('report.high_spike_meals') !== 'report.high_spike_meals' ? t('report.high_spike_meals') : 'Meals Associated with High Glucose Spikes',
        stableMeals: t('report.stable_meals') !== 'report.stable_meals' ? t('report.stable_meals') : 'Meals with Stable Glucose Response',
        lanaSummary: t('report.lana_summary') !== 'report.lana_summary' ? t('report.lana_summary') : "Lana's Health Summary",
        noData: t('report.no_data') !== 'report.no_data' ? t('report.no_data') : 'Not enough data for personalized insights.',
        footer1: t('report.footer1') !== 'report.footer1' ? t('report.footer1') : 'Generated by PlateMind - AI for everyday diabetes decisions',
        footer2: t('report.footer2') !== 'report.footer2' ? t('report.footer2') : 'This report is for informational purposes only. Consult your healthcare provider for medical advice.',
        postMeal: t('report.post_meal') !== 'report.post_meal' ? t('report.post_meal') : 'post-meal',
        carbs: t('report.carbs') !== 'report.carbs' ? t('report.carbs') : 'carbs',
      };
      
      // Create PDF
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      let y = 20;

      // Header
      pdf.setFillColor(255, 122, 0);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont(undefined, 'bold');
      pdf.text(pdfText.title, 20, 28);
      
      y = 55;
      
      // User info
      pdf.setTextColor(31, 41, 51);
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text(`${pdfText.preparedFor}: ${reportData.user_name}`, 20, y);
      y += 10;
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      const startFormatted = new Date(reportData.date_range.start).toLocaleDateString();
      const endFormatted = new Date(reportData.date_range.end).toLocaleDateString();
      pdf.text(`${pdfText.reportPeriod}: ${startFormatted} - ${endFormatted}`, 20, y);
      y += 6;
      pdf.text(`${pdfText.generated}: ${new Date().toLocaleDateString()}`, 20, y);
      y += 18;
      
      // Glucose Summary Section
      pdf.setFillColor(255, 247, 240);
      pdf.rect(15, y - 5, pageWidth - 30, 50, 'F');
      
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(31, 41, 51);
      pdf.text(pdfText.glucoseSummary, 20, y + 5);
      y += 18;
      
      pdf.setTextColor(31, 41, 51);
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'normal');
      
      const stats = reportData.glucose_stats;
      pdf.text(`${pdfText.avgGlucose}: ${stats.average} mg/dL`, 25, y);
      pdf.text(`${pdfText.highestReading}: ${stats.highest} mg/dL`, 115, y);
      y += 10;
      pdf.text(`${pdfText.lowestReading}: ${stats.lowest} mg/dL`, 25, y);
      pdf.text(`${pdfText.totalReadings}: ${stats.total_readings}`, 115, y);
      y += 10;
      
      // Time in range indicator
      const timeInRange = stats.total_readings > 0 
        ? Math.round((stats.in_range / stats.total_readings) * 100) 
        : 0;
      pdf.setFont(undefined, 'bold');
      pdf.text(`${pdfText.timeInRange}: ${timeInRange}%`, 25, y);
      y += 25;
      
      // Meal Insights Section
      pdf.setFillColor(255, 247, 240);
      pdf.rect(15, y - 5, pageWidth - 30, 40, 'F');
      
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(31, 41, 51);
      pdf.text(pdfText.mealInsights, 20, y + 5);
      y += 18;
      
      pdf.setTextColor(31, 41, 51);
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'normal');
      
      const mealStats = reportData.meal_stats;
      pdf.text(`${pdfText.totalMeals}: ${mealStats.total_meals}`, 25, y);
      pdf.text(`${pdfText.avgCarbs}: ${mealStats.average_carbs_per_meal}g`, 115, y);
      y += 10;
      pdf.text(`${pdfText.totalCarbs}: ${mealStats.total_carbs}g`, 25, y);
      y += 25;
      
      // High Spike Meals
      if (reportData.high_spike_meals && reportData.high_spike_meals.length > 0) {
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(231, 111, 81);
        pdf.text(pdfText.highSpikeMeals, 20, y);
        y += 10;
        
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(31, 41, 51);
        pdf.setFontSize(10);
        
        reportData.high_spike_meals.slice(0, 3).forEach(meal => {
          const mealDate = new Date(meal.timestamp).toLocaleDateString();
          pdf.text(`- ${mealDate}: ${meal.carbs}g ${pdfText.carbs} -> ${meal.avg_post_glucose} mg/dL ${pdfText.postMeal}`, 25, y);
          y += 8;
        });
        y += 12;
      }
      
      // Stable Meals
      if (reportData.stable_meals && reportData.stable_meals.length > 0) {
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(79, 138, 101);
        pdf.text(pdfText.stableMeals, 20, y);
        y += 10;
        
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(31, 41, 51);
        pdf.setFontSize(10);
        
        reportData.stable_meals.slice(0, 3).forEach(meal => {
          const mealDate = new Date(meal.timestamp).toLocaleDateString();
          pdf.text(`- ${mealDate}: ${meal.carbs}g ${pdfText.carbs} -> ${meal.avg_post_glucose} mg/dL ${pdfText.postMeal}`, 25, y);
          y += 8;
        });
        y += 12;
      }
      
      // AI Summary from Lana - handle page overflow properly
      const aiSummary = reportData.ai_summary || pdfText.noData;
      // Clean markdown and special characters from summary
      const cleanSummary = aiSummary
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')
        .replace(/##/g, '')
        .replace(/[^\x00-\x7F]/g, '') // Remove non-ASCII characters that cause garbled text
        .trim();
      const summaryLines = pdf.splitTextToSize(cleanSummary, pageWidth - 50);
      
      // Calculate if we need to start on a new page
      const estimatedHeight = summaryLines.length * 7 + 35;
      const remainingSpace = 280 - y;
      
      if (remainingSpace < 80) {
        // Not enough space, start on new page
        pdf.addPage();
        y = 20;
      }
      
      // Draw Lana's Summary header
      pdf.setFillColor(255, 247, 240);
      const headerBoxHeight = 30;
      pdf.roundedRect(15, y - 5, pageWidth - 30, headerBoxHeight, 3, 3, 'F');
      
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(31, 41, 51);
      pdf.text(pdfText.lanaSummary, 20, y + 12);
      y += headerBoxHeight + 5;
      
      // Draw summary text without background (to avoid overlap issues)
      pdf.setTextColor(31, 41, 51);
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      
      // Render each line, handling page breaks
      summaryLines.forEach((line, index) => {
        if (y > 270) {
          pdf.addPage();
          y = 20;
        }
        pdf.text(line, 20, y);
        y += 7;
      });
      
      y += 10; // Add some space after summary
      
      // Footer
      y = pdf.internal.pageSize.getHeight() - 20;
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(pdfText.footer1, pageWidth / 2, y, { align: 'center' });
      pdf.text(pdfText.footer2, pageWidth / 2, y + 6, { align: 'center' });
      
      // Save PDF
      const fileName = `PlateMind_Report_${startFormatted.replace(/\//g, '-')}_to_${endFormatted.replace(/\//g, '-')}.pdf`;
      pdf.save(fileName);
      
      toast.success(t('insights.report_downloaded') || "Report downloaded successfully!");
      setShowReportDialog(false);
      
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error(t('common.error') || "Failed to generate report");
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-orange-100 bg-background/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-muted-foreground hover:text-secondary transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold text-secondary">{t('insights.title')}</h1>
          </div>
          <Button
            onClick={() => setShowReportDialog(true)}
            variant="outline"
            className="rounded-full border-primary text-primary hover:bg-primary hover:text-white"
            data-testid="download-report-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {t('insights.download_report')}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {loading ? (
          <div className="space-y-6">
            <div className="flex items-center justify-center gap-3 py-4">
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
              <p className="text-muted-foreground text-sm">Analyzing your health data...</p>
            </div>
            {/* Stat cards skeleton */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse bg-white rounded-3xl border border-orange-100/30 p-4 space-y-2">
                  <div className="h-8 bg-orange-100/40 rounded-lg w-16 mx-auto" />
                  <div className="h-3 bg-orange-100/30 rounded w-20 mx-auto" />
                </div>
              ))}
            </div>
            {/* Chart skeleton */}
            <div className="animate-pulse bg-white rounded-3xl border border-orange-100/30 p-6 space-y-4">
              <div className="h-4 bg-orange-100/40 rounded w-32" />
              <div className="h-48 bg-orange-50/50 rounded-2xl" />
            </div>
            {/* AI insight skeleton */}
            <div className="animate-pulse bg-primary/5 rounded-3xl p-6 space-y-3">
              <div className="h-4 bg-primary/10 rounded w-40" />
              <div className="h-3 bg-primary/10 rounded w-full" />
              <div className="h-3 bg-primary/10 rounded w-3/4" />
              <div className="h-3 bg-primary/10 rounded w-5/6" />
            </div>
          </div>
        ) : !insights || insights.readings_count === 0 ? (
          <Card className="bg-white rounded-3xl border border-orange-100/50 p-8 text-center space-y-4">
            <div className="w-20 h-20 rounded-full bg-orange-50 mx-auto flex items-center justify-center">
              <LineChart className="w-10 h-10 text-primary/50" />
            </div>
            <h2 className="font-heading text-xl font-semibold text-secondary">{t('insights.no_data_yet')}</h2>
            <p className="text-muted-foreground max-w-sm mx-auto">
              {t('insights.start_logging_desc')}
            </p>
            <div className="flex gap-3 justify-center pt-4">
              <Link to="/glucose">
                <button className="px-6 py-3 rounded-full bg-primary text-white font-medium" data-testid="start-logging-btn">
                  {t('insights.log_glucose_btn')}
                </button>
              </Link>
              <Link to="/meal-analysis">
                <button className="px-6 py-3 rounded-full border-2 border-border font-medium" data-testid="analyze-meal-btn">
                  {t('insights.analyze_meal_btn')}
                </button>
              </Link>
            </div>
          </Card>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-white rounded-3xl border border-orange-100/50 p-4 text-center">
                <p className="text-3xl font-bold text-secondary">{Math.round(insights.average_glucose)}</p>
                <p className="text-sm text-muted-foreground">{t('insights.avg_mg_dl')}</p>
              </Card>
              <Card className="bg-success/10 rounded-3xl border-none p-4 text-center">
                <div className="flex items-center justify-center gap-1">
                  <CheckCircle className="w-5 h-5 text-success" />
                  <p className="text-3xl font-bold text-success">{insights.in_range_readings}</p>
                </div>
                <p className="text-sm text-success/80">{t('insights.in_range')}</p>
              </Card>
              <Card className="bg-warning/10 rounded-3xl border-none p-4 text-center">
                <div className="flex items-center justify-center gap-1">
                  <TrendingUp className="w-5 h-5 text-warning" />
                  <p className="text-3xl font-bold text-warning">{insights.high_readings}</p>
                </div>
                <p className="text-sm text-warning/80">{t('insights.high_readings')}</p>
              </Card>
              <Card className="bg-destructive/10 rounded-3xl border-none p-4 text-center">
                <div className="flex items-center justify-center gap-1">
                  <TrendingDown className="w-5 h-5 text-destructive" />
                  <p className="text-3xl font-bold text-destructive">{insights.low_readings}</p>
                </div>
                <p className="text-sm text-destructive/80">{t('insights.low_readings')}</p>
              </Card>
            </div>

            {/* Glucose Trend Chart */}
            {chartData.length > 0 && (
              <Card className="bg-white rounded-3xl border border-orange-100/50 p-6 space-y-4">
                <h2 className="font-heading text-lg font-semibold text-secondary">{t('insights.glucose_trend')}</h2>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorGlucose" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#FF7A00" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#FF7A00" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EBE0D6" />
                      <XAxis dataKey="time" stroke="#52606D" fontSize={12} />
                      <YAxis stroke="#52606D" fontSize={12} domain={[40, 220]} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #EBE0D6',
                          borderRadius: '12px',
                          padding: '8px 12px',
                        }}
                        formatter={(value) => [`${value} mg/dL`, 'Glucose']}
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#FF7A00"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorGlucose)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-success" />
                    {t('insights.target_range')}
                  </span>
                </div>
              </Card>
            )}

            {/* Daily Pattern */}
            {dailyPatternData.length > 0 && (
              <Card className="bg-white rounded-3xl border border-orange-100/50 p-6 space-y-4">
                <h2 className="font-heading text-lg font-semibold text-secondary">{t('insights.avg_by_reading_type')}</h2>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyPatternData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#EBE0D6" />
                      <XAxis type="number" stroke="#52606D" fontSize={12} domain={[0, 'auto']} />
                      <YAxis dataKey="name" type="category" stroke="#52606D" fontSize={11} width={80} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #EBE0D6',
                          borderRadius: '12px',
                          padding: '8px 12px',
                        }}
                        formatter={(value) => [`${value} mg/dL`, 'Average']}
                      />
                      <Bar dataKey="value" fill="#FF7A00" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}

            {/* AI Insight */}
            <Card className="bg-primary/5 rounded-3xl border-none p-6 space-y-4">
              <div className="flex items-center gap-2 border-b border-primary/10 pb-3">
                <Info className="w-6 h-6 text-primary" />
                <h2 className="font-heading text-lg font-semibold text-secondary">{t('insights.ai_insight')}</h2>
              </div>
              {(() => {
                // Clean and parse the AI insight text
                const rawInsight = insights.ai_insight || "";
                const cleanText = rawInsight
                  .replace(/\*\*/g, '')
                  .replace(/##/g, '')
                  .replace(/#/g, '')
                  .replace(/\*/g, '')
                  .trim();
                
                // Try to extract sections
                const sections = {
                  summary: "",
                  concern: "",
                  tips: []
                };
                
                // Split by common section patterns
                const lines = cleanText.split('\n').filter(l => l.trim());
                let currentSection = 'summary';
                
                for (const line of lines) {
                  const lowerLine = line.toLowerCase();
                  const cleanLine = line.replace(/^[-•]\s*/, '').trim();
                  
                  if (lowerLine.includes('good news') || lowerLine.includes('summary') || lowerLine.includes('overview')) {
                    currentSection = 'summary';
                    if (cleanLine && !lowerLine.includes(':')) sections.summary += cleanLine + ' ';
                    continue;
                  } else if (lowerLine.includes('concern') || lowerLine.includes('attention') || lowerLine.includes('key concern') || lowerLine.includes('watch out')) {
                    currentSection = 'concern';
                    if (cleanLine && !lowerLine.includes(':')) sections.concern += cleanLine + ' ';
                    continue;
                  } else if (lowerLine.includes('action') || lowerLine.includes('tip') || lowerLine.includes('step') || lowerLine.includes('suggestion') || lowerLine.includes('what you can do')) {
                    currentSection = 'tips';
                    continue;
                  }
                  
                  if (cleanLine) {
                    if (currentSection === 'tips') {
                      sections.tips.push(cleanLine);
                    } else if (currentSection === 'concern') {
                      sections.concern += cleanLine + ' ';
                    } else {
                      sections.summary += cleanLine + ' ';
                    }
                  }
                }
                
                // If no sections were parsed, just show as summary
                if (!sections.summary && !sections.concern && sections.tips.length === 0) {
                  sections.summary = cleanText;
                }
                
                return (
                  <div className="space-y-4">
                    {sections.summary && (
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-primary">{t('insights.summary')}</p>
                        <p className="text-sm text-secondary leading-relaxed">{sections.summary.trim()}</p>
                      </div>
                    )}
                    
                    {sections.concern && (
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-warning">{t('insights.key_concern')}</p>
                        <p className="text-sm text-secondary leading-relaxed">{sections.concern.trim()}</p>
                      </div>
                    )}
                    
                    {sections.tips.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-primary">{t('insights.action_steps')}</p>
                        <ul className="space-y-2">
                          {sections.tips.slice(0, 4).map((tip, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-secondary">
                              <span className="text-primary mt-0.5 flex-shrink-0">•</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}
            </Card>

            {/* Summary Stats */}
            <Card className="bg-white rounded-3xl border border-orange-100/50 p-6 space-y-4">
              <h2 className="font-heading text-lg font-semibold text-secondary">{t('insights.summary')}</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl bg-orange-50/50">
                  <p className="text-sm text-muted-foreground">{t('insights.total_readings')}</p>
                  <p className="text-2xl font-bold text-secondary">{insights.readings_count}</p>
                </div>
                <div className="p-4 rounded-2xl bg-orange-50/50">
                  <p className="text-sm text-muted-foreground">{t('insights.time_in_range')}</p>
                  <p className="text-2xl font-bold text-success">
                    {Math.round((insights.in_range_readings / insights.readings_count) * 100)}%
                  </p>
                </div>
              </div>
            </Card>
          </>
        )}
      </main>

      {/* Download Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-secondary flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {t('insights.download_report_dialog_title')}
            </DialogTitle>
            <DialogDescription>
              {t('insights.download_report_dialog_desc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('insights.select_time_range')}</Label>
              <Select value={reportRange} onValueChange={setReportRange}>
                <SelectTrigger className="rounded-xl" data-testid="report-range-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">{t('insights.last_7_days')}</SelectItem>
                  <SelectItem value="14">{t('insights.last_14_days')}</SelectItem>
                  <SelectItem value="30">{t('insights.last_30_days')}</SelectItem>
                  <SelectItem value="custom">{t('insights.custom_range')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {reportRange === "custom" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start-date">{t('insights.start_date')}</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={customRange.start}
                    onChange={(e) => setCustomRange({ ...customRange, start: e.target.value })}
                    className="rounded-xl"
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-date">{t('insights.end_date')}</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={customRange.end}
                    onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })}
                    className="rounded-xl"
                    data-testid="end-date-input"
                  />
                </div>
              </div>
            )}
            
            <Card className="bg-orange-50/50 rounded-2xl p-4">
              <p className="text-sm text-muted-foreground">
                <strong>{t('insights.report_includes')}</strong>
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• {t('insights.report_item_1')}</li>
                <li>• {t('insights.report_item_2')}</li>
                <li>• {t('insights.report_item_3')}</li>
              </ul>
            </Card>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowReportDialog(false)}
              className="rounded-full"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={generatePDF}
              disabled={reportLoading || (reportRange === "custom" && (!customRange.start || !customRange.end))}
              className="rounded-full bg-primary text-white hover:bg-primary/90"
              data-testid="generate-report-btn"
            >
              {reportLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('insights.generating')}
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  {t('insights.download_pdf')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default Insights;
